<!-- Header -->
<div class="flex items-center gap-2 p-3 border-b border-base-300">
  <h3 class="font-semibold text-lg flex-1">Staging-Konfigurationen</h3>
  @if (!showForm()) {
    <button class="bm-btn bm-btn-primary bm-btn-sm" (click)="openCreateForm()">
      <fa-icon [icon]="faPlus" class="mr-1" /> Neue Config
    </button>
  }
</div>

<!-- Formular (Erstellen/Bearbeiten) -->
@if (showForm()) {
  <div class="flex-1 overflow-y-auto p-4">
    <div class="bm-card bm-card-border bg-base-100 mb-4">
      <div class="bm-card-body">
        <div class="bm-card-title">{{ editingConfigId() ? 'Config bearbeiten' : 'Neue Config erstellen' }}</div>

        <!-- Grunddaten -->
        <div class="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label class="bm-label mb-1">Name *</label>
            <input type="text" class="bm-input bm-input-sm w-full" [(ngModel)]="formName" placeholder="z.B. dm Kassenrechner" />
          </div>
          <div>
            <label class="bm-label mb-1">Artikelvorlage</label>
            <div class="relative">
              <input
                type="text"
                class="bm-input bm-input-sm w-full"
                [(ngModel)]="articleTemplateSearch"
                (input)="onTemplateSearchInput()"
                placeholder="Vorlage suchen..."
              />
              @if (formArticleTemplateId) {
                <button class="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/50 hover:text-error" (click)="clearTemplate()">
                  <fa-icon [icon]="faTimes" />
                </button>
              }
              @if (showTemplateDropdown() && filteredTemplates().length > 0) {
                <div class="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded shadow-lg max-h-48 overflow-y-auto">
                  @for (t of filteredTemplates(); track t.id) {
                    <div class="px-3 py-2 cursor-pointer hover:bg-base-200 text-sm" (click)="selectTemplate(t)">
                      {{ t.description }}
                    </div>
                  }
                </div>
              }
            </div>
          </div>
          <div class="col-span-2">
            <label class="bm-label mb-1">Beschreibung</label>
            <input type="text" class="bm-input bm-input-sm w-full" [(ngModel)]="formDescription" />
          </div>
          <div>
            <label class="bm-label mb-1">Paketgröße (max. Artikel pro Paket)</label>
            <input type="number" class="bm-input bm-input-sm w-full" [(ngModel)]="formPackageSize" placeholder="z.B. 7" min="1" />
          </div>
          <div>
            <label class="bm-label mb-1">Staging-Plätze</label>
            <input type="number" class="bm-input bm-input-sm w-full" [(ngModel)]="formStagingSlotCount" min="1" max="50" placeholder="z.B. 10" />
          </div>
          <div class="col-span-2">
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" class="bm-checkbox bm-checkbox-sm" [(ngModel)]="formAuthorizeSerialNumbers" />
              <span class="text-sm">Seriennummern müssen vor dem Staging freigegeben werden</span>
            </label>
          </div>
        </div>

        <!-- Ticket-Zusammenfassung (wöchentliches Staging-Volumen-Ticket) -->
        <div class="border-t border-base-300 pt-3 mt-3">
          <h4 class="font-semibold mb-1">Ticket-Zusammenfassung</h4>
          <p class="text-xs text-base-content/60 mb-2">
            Für diese Config wird montags ein Ticket „Staging Volumen KW XX" angelegt und
            täglich aktualisiert. Filiale und Priorität bestimmen, wo das Ticket erscheint.
          </p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="bm-label mb-1">Priorität</label>
              <select class="bm-select bm-select-sm w-full" [(ngModel)]="formSummaryPriorityId">
                <option [ngValue]="null">Standard (Staging-Priorität des Projekts)</option>
                @for (p of priorities(); track p.id) {
                  <option [ngValue]="p.id">{{ p.name }}</option>
                }
              </select>
            </div>
            <div>
              <label class="bm-label mb-1">Filiale</label>
              <div class="flex items-center gap-2">
                <button type="button" class="bm-btn bm-btn-sm flex-1 justify-start" (click)="openStorePicker()">
                  {{ formSummaryStoreName || 'Filiale auswählen…' }}
                </button>
                @if (formSummaryStoreId) {
                  <button type="button" class="bm-btn bm-btn-ghost bm-btn-sm" (click)="clearSummaryStore()">
                    <fa-icon [icon]="faTimes" />
                  </button>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Wochenzuordnung (Weekly Schedule) -->
        <div class="border-t border-base-300 pt-3 mt-3">
          <div class="flex items-center justify-between mb-2">
            <h4 class="font-semibold">Wochenzuordnung</h4>
            <button class="bm-btn bm-btn-ghost bm-btn-xs" (click)="addWeeklyScheduleEntry()">
              <fa-icon [icon]="faPlus" class="mr-1"></fa-icon> KW hinzufügen
            </button>
          </div>
          @if (formWeeklySchedule.length > 0) {
            <div class="overflow-x-auto">
              <table class="table table-xs w-full">
                <thead>
                  <tr class="text-xs text-base-content/60">
                    <th>Jahr</th>
                    <th>Staging KW</th>
                    <th>Wochenfarbe</th>
                    <th>Versand KW</th>
                    <th>Einbau KW</th>
                    <th>Frist</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (entry of formWeeklySchedule; track $index; let i = $index) {
                    <tr>
                      <td><input type="number" class="bm-input bm-input-xs w-16" [(ngModel)]="entry.year" min="2024" max="2035" /></td>
                      <td><input type="number" class="bm-input bm-input-xs w-16" [(ngModel)]="entry.staging_kw" min="1" max="53" /></td>
                      <td><input type="text" class="bm-input bm-input-xs w-24" [(ngModel)]="entry.week_color" placeholder="z.B. rot" /></td>
                      <td><input type="number" class="bm-input bm-input-xs w-16" [(ngModel)]="entry.shipping_kw" min="1" max="53" /></td>
                      <td><input type="number" class="bm-input bm-input-xs w-16" [(ngModel)]="entry.installation_kw" min="1" max="53" /></td>
                      <td><input type="date" class="bm-input bm-input-xs w-32" [(ngModel)]="entry.staging_deadline" /></td>
                      <td>
                        <button class="bm-btn bm-btn-ghost bm-btn-xs bm-btn-square" (click)="removeWeeklyScheduleEntry(i)">
                          <fa-icon [icon]="faTimes"></fa-icon>
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <p class="text-sm text-base-content/60">Keine Wochenzuordnung definiert.</p>
          }
        </div>

        <!-- Step-Editor -->
        <div class="border-t border-base-300 pt-3">
          <div class="flex items-center justify-between mb-2">
            <h4 class="font-semibold">Staging-Schritte</h4>
            <div class="flex items-center gap-1">
              <button
                class="bm-btn bm-btn-ghost bm-btn-xs"
                [class.bm-btn-active]="!stepsJsonMode()"
                (click)="stepsJsonMode() && toggleStepsJsonMode()"
              >Editor</button>
              <button
                class="bm-btn bm-btn-ghost bm-btn-xs"
                [class.bm-btn-active]="stepsJsonMode()"
                (click)="!stepsJsonMode() && toggleStepsJsonMode()"
              >JSON</button>
              @if (!stepsJsonMode()) {
                <button class="bm-btn bm-btn-ghost bm-btn-sm" (click)="addStep()">
                  <fa-icon [icon]="faPlus" class="mr-1" /> Schritt
                </button>
              }
            </div>
          </div>

          @if (stepsJsonMode()) {
            <textarea
              class="bm-input bm-input-sm w-full font-mono text-xs"
              style="min-height: 300px; resize: vertical;"
              [(ngModel)]="stepsJsonText"
            ></textarea>
            @if (stepsJsonError()) {
              <div class="text-xs text-error mt-1">{{ stepsJsonError() }}</div>
            }
          } @else {

          @for (step of formSteps; track step.number; let si = $index) {
            <div class="bm-card bm-card-border bm-card-sm mb-2 bg-base-200/50">
              <div class="p-3">
                <!-- Schritt-Header -->
                <div class="flex items-center gap-2 mb-2">
                  <span class="bm-badge bm-badge-neutral bm-badge-xs">{{ step.number }}</span>
                  <input type="text" class="bm-input bm-input-sm flex-1" [(ngModel)]="step.title" placeholder="Schritt-Titel..." />
                  <label class="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" class="bm-checkbox bm-checkbox-xs" [(ngModel)]="step.enabled" />
                    <span class="text-xs">An</span>
                  </label>
                  <button class="bm-btn bm-btn-ghost bm-btn-sm bm-btn-square" [disabled]="si === 0" (click)="moveStep(si, 'up')">
                    <fa-icon [icon]="faChevronUp" />
                  </button>
                  <button class="bm-btn bm-btn-ghost bm-btn-sm bm-btn-square" [disabled]="si === formSteps.length - 1" (click)="moveStep(si, 'down')">
                    <fa-icon [icon]="faChevronDown" />
                  </button>
                  <button class="bm-btn bm-btn-ghost bm-btn-sm bm-btn-square text-error" (click)="removeStep(si)">
                    <fa-icon [icon]="faTrash" />
                  </button>
                </div>

                <!-- Items -->
                @for (item of step.items; track $index; let ii = $index) {
                  <div class="flex items-start gap-2 mb-1 ml-6">
                    <select class="bm-select bm-select-xs w-36 shrink-0" [(ngModel)]="item.type" (ngModelChange)="onStepItemTypeChange(item)">
                      @for (t of stepItemTypes; track t.value) {
                        <option [value]="t.value">{{ t.label }}</option>
                      }
                    </select>
                    <input type="text" class="bm-input bm-input-sm flex-1" [(ngModel)]="item.label" placeholder="Beschreibung..." />
                    @if (item.type === 'select') {
                      <input type="text" class="bm-input bm-input-sm w-40"
                        [ngModel]="joinOptions(item.options)"
                        (ngModelChange)="item.options = parseOptions($event)"
                        placeholder="Option1, Option2, ..." />
                    }
                    @if (item.type === 'pdf_generate' && item.pdf_config) {
                      <input type="number" class="bm-input bm-input-sm w-20" [(ngModel)]="item.pdf_config.width_mm" placeholder="mm" />
                    }
                    <button class="bm-btn bm-btn-ghost bm-btn-sm bm-btn-square text-error shrink-0" (click)="removeStepItem(si, ii)">
                      <fa-icon [icon]="faTimes" />
                    </button>
                  </div>
                  <!-- Checklist-Konfiguration -->
                  @if (item.type === 'checklist') {
                    <div class="ml-6 mb-2 pl-4 border-l-2 border-base-300">
                      <!-- Check-Items -->
                      @for (ci of item.checklist_items || []; track $index; let cii = $index) {
                        <div class="flex items-center gap-2 mb-1">
                          <span class="text-xs text-base-content/40 w-5 shrink-0">{{ cii + 1 }}.</span>
                          <input type="text" class="bm-input bm-input-sm flex-1" [(ngModel)]="item.checklist_items![cii]" placeholder="Check-Item..." />
                          <button class="bm-btn bm-btn-ghost bm-btn-xs bm-btn-square text-error" (click)="removeChecklistItem(item, cii)">
                            <fa-icon [icon]="faTimes" />
                          </button>
                        </div>
                      }
                      <button class="bm-btn bm-btn-ghost bm-btn-xs mt-1" (click)="addChecklistItem(item)">
                        <fa-icon [icon]="faPlus" class="mr-1" /> Check-Item
                      </button>
                      <!-- Benachrichtigungs-Optionen -->
                      <div class="flex items-center gap-3 mt-2 pt-2 border-t border-base-200">
                        <label class="flex items-center gap-2 cursor-pointer select-none text-sm">
                          <input type="checkbox" class="w-4 h-4 rounded accent-primary" [(ngModel)]="item.notify_on_incomplete" />
                          Mail bei Unvollständigkeit
                        </label>
                        @if (item.notify_on_incomplete) {
                          <input type="email" class="bm-input bm-input-sm w-56" [(ngModel)]="item.notify_email" placeholder="helpdesk@bentomax.de" />
                        }
                      </div>
                    </div>
                  }
                }
                <button class="bm-btn bm-btn-ghost bm-btn-sm ml-6 mt-1" (click)="addStepItem(si)">
                  <fa-icon [icon]="faPlus" class="mr-1" /> Item
                </button>
              </div>
            </div>
          }

          @if (formSteps.length === 0) {
            <div class="text-center text-base-content/50 py-4 text-sm">
              Noch keine Schritte definiert. Klicke "+ Schritt" um zu beginnen.
            </div>
          }

          } <!-- Ende @else (Editor-View) -->
        </div>

        <!-- Footer -->
        <div class="flex gap-2 justify-end mt-4 border-t border-base-300 pt-3">
          <button class="bm-btn bm-btn-ghost bm-btn-sm" (click)="cancelForm()">Abbrechen</button>
          <button class="bm-btn bm-btn-primary bm-btn-sm" [disabled]="!formName.trim()" (click)="saveForm()">
            <fa-icon [icon]="faSave" class="mr-1" /> Speichern
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Config-Liste -->
@if (!showForm()) {
  <div class="flex-1 overflow-y-auto p-4">
    @if (isLoading()) {
      <div class="flex items-center justify-center py-8">
        <span class="loading loading-spinner loading-md"></span>
      </div>
    }

    @if (!isLoading() && configs().length === 0) {
      <div class="text-center text-base-content/50 py-8">
        Noch keine Staging-Konfigurationen für dieses Projekt.
      </div>
    }

    <div class="flex flex-col gap-3">
      @for (config of configs(); track config.id) {
        <div class="bm-card bm-card-border bg-base-100" [class.opacity-50]="!config.enabled">
          <div class="bm-card-body p-4">
            <!-- Header -->
            <div class="flex items-center gap-3 mb-2">
              <label class="cursor-pointer" (click)="toggleEnabled(config); $event.stopPropagation()">
                <input type="checkbox" class="bm-toggle bm-toggle-sm bm-toggle-primary" [checked]="config.enabled" />
              </label>
              <h4 class="font-semibold flex-1">{{ config.name }}</h4>
              @if (!config.article_template_id) {
                <span class="bm-badge bm-badge-warning bm-badge-xs">Kein Artikel — ungültig</span>
              }
              <button class="bm-btn bm-btn-ghost bm-btn-sm bm-btn-square" (click)="openEditForm(config)">
                <fa-icon [icon]="faEdit" />
              </button>
              <button class="bm-btn bm-btn-ghost bm-btn-sm bm-btn-square text-error" (click)="deleteConfig(config)">
                <fa-icon [icon]="faTrash" />
              </button>
            </div>

            <!-- Artikel (eigene Zeile) -->
            <div class="text-sm mb-1">
              <span class="text-base-content/50">Artikel:</span>
              <span class="ml-1 font-medium">{{ config.article_template_description || '-' }}</span>
            </div>

            <!-- Details -->
            <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm mb-2">
              <div>
                <span class="text-base-content/50">Paketgröße:</span>
                <span class="ml-1">{{ config.package_size || '-' }} Artikel/Paket</span>
              </div>
              <div>
                <span class="text-base-content/50">Excel-Daten:</span>
                <span class="ml-1">{{ config.excel_data_count }} Zeilen</span>
              </div>
            </div>

            <!-- Schritte (kompakt, nur Anzeige) -->
            @if (config.steps && config.steps.length > 0) {
              <div class="flex flex-wrap gap-1 mb-2">
                @for (step of config.steps; track step.number) {
                  <span
                    class="bm-badge"
                    [class.bm-badge-neutral]="step.enabled"
                    [class.opacity-40]="!step.enabled"
                  >
                    {{ step.number }}. {{ step.title || 'Schritt' }}
                  </span>
                }
              </div>
            }

            <!-- Excel-Upload & Ansicht -->
            <div class="flex items-center gap-2 border-t border-base-200 pt-2">
              <span class="text-sm">SN/MAC Relationen:</span>
              <span class="text-xs text-success">{{ config.excel_data_count }}</span>
              <div class="ml-auto flex items-center gap-1">
                <label class="bm-btn bm-btn-ghost bm-btn-sm cursor-pointer">
                  <fa-icon [icon]="faUpload" class="mr-1" /> Excel hochladen
                  <input type="file" class="hidden" accept=".xlsx,.xls" (change)="onExcelFileSelected($event, config.id)" />
                </label>
              @if (config.excel_data_count > 0) {
                <button class="bm-btn bm-btn-ghost bm-btn-sm" (click)="toggleExcelView(config.id)">
                  {{ excelViewConfigId() === config.id ? 'Ausblenden' : 'Anzeigen' }}
                </button>
              }
              </div>
            </div>

            <!-- Excel-Daten Tabelle -->
            @if (excelViewConfigId() === config.id) {
              @if (isExcelLoading()) {
                <div class="flex justify-center py-4">
                  <span class="loading loading-spinner loading-md"></span>
                </div>
              } @else {
                <div class="border-t border-base-200 pt-2 mt-2">
                  <!-- Filter-Toggle + Suche -->
                  <div class="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <input
                      type="text"
                      class="bm-input bm-input-sm flex-1 min-w-[200px]"
                      placeholder="Suche..."
                      [ngModel]="excelGridQuickFilter()"
                      (ngModelChange)="onExcelQuickFilterChange($event)"
                    />
                    <span class="text-xs text-base-content/60">
                      {{ excelData().length }} Zeilen
                    </span>
                    <label class="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="checkbox" class="bm-toggle bm-toggle-sm bm-toggle-primary" [checked]="hideStaged()" (change)="toggleHideStaged()" />
                      Gestaged ausblenden
                    </label>
                  </div>
                  <ag-grid-angular
                    class="w-full"
                    style="height: 24rem;"
                    [theme]="gridTheme"
                    [loadThemeGoogleFonts]="true"
                    [rowData]="excelGridRowData()"
                    [columnDefs]="excelColumnDefs()"
                    [defaultColDef]="excelDefaultColDef"
                    [localeText]="gridLocale"
                    [singleClickEdit]="true"
                    [stopEditingWhenCellsLoseFocus]="true"
                    [isExternalFilterPresent]="isExcelExternalFilterPresent"
                    [doesExternalFilterPass]="doesExcelExternalFilterPass"
                    (gridReady)="onExcelGridReady($event)"
                    (cellValueChanged)="onExcelCellValueChanged($event)"
                  />

                  <!-- Bulk-Freigabe -->
                  @if (excelAuthorize()) {
                    <div class="mt-3 border-t border-base-200 pt-3">
                      <div class="flex items-center justify-between mb-1">
                        <label class="bm-label text-sm">Seriennummern freigeben (;-getrennt)</label>
                        <button class="bm-btn bm-btn-ghost bm-btn-sm" (click)="authorizeAll()">
                          Alle freigeben
                        </button>
                      </div>
                      <div class="flex gap-2">
                        <textarea
                          class="bm-input bm-input-sm flex-1 min-h-[60px]"
                          [(ngModel)]="bulkAuthorizeInput"
                          placeholder="SN001; SN002; SN003"
                        ></textarea>
                        <button
                          class="bm-btn bm-btn-primary bm-btn-sm shrink-0 self-end"
                          [disabled]="!bulkAuthorizeInput.trim()"
                          (click)="submitBulkAuthorize()"
                        >
                          Freigeben
                        </button>
                      </div>
                    </div>
                  }
                </div>
              }
            }

            <!-- Staging-Records -->
            <div class="flex items-center gap-2 border-t border-base-200 pt-2">
              <span class="text-sm">Records:</span>
              <span class="text-xs text-success">{{ config.staging_records_count }}</span>
              <div class="ml-auto flex items-center gap-1">
                <button class="bm-btn bm-btn-ghost bm-btn-sm" (click)="openExportDialog(config.id)">
                  <fa-icon [icon]="faDownload" class="mr-1" /> Excel Export
                </button>
                <button class="bm-btn bm-btn-ghost bm-btn-sm" (click)="toggleRecordsView(config.id)">
                  {{ recordsViewConfigId() === config.id ? 'Ausblenden' : 'Anzeigen' }}
                </button>
              </div>
            </div>

            @if (recordsViewConfigId() === config.id) {
              @if (isRecordsLoading()) {
                <div class="flex justify-center py-4">
                  <span class="loading loading-spinner loading-md"></span>
                </div>
              } @else if (recordsList().length === 0) {
                <div class="text-sm text-base-content/50 py-2">Noch keine Records vorhanden.</div>
              } @else {
                <div class="border-t border-base-200 pt-2 mt-1">
                  <div class="flex items-center justify-between mb-2 gap-2">
                    <input
                      type="text"
                      class="bm-input bm-input-sm flex-1 min-w-[200px]"
                      placeholder="Suche..."
                      [ngModel]="recordsGridQuickFilter()"
                      (ngModelChange)="onRecordsQuickFilterChange($event)"
                    />
                    <span class="text-xs text-base-content/60">{{ recordsList().length }} Records</span>
                  </div>
                  <ag-grid-angular
                    class="w-full"
                    style="height: 24rem;"
                    [theme]="gridTheme"
                    [loadThemeGoogleFonts]="true"
                    [rowData]="recordsList()"
                    [columnDefs]="recordsColumnDefs()"
                    [defaultColDef]="recordsDefaultColDef"
                    [localeText]="gridLocale"
                    (gridReady)="onRecordsGridReady($event)"
                  />
                </div>
              }
            }

            <!-- Ungenutzte Reserve -->
            <div class="flex items-center gap-2 border-t border-base-200 pt-2">
              <span class="text-sm">Ungenutzte Reserve:</span>
              <button class="bm-btn bm-btn-ghost bm-btn-sm ml-auto" (click)="toggleReservesView(config.id)">
                {{ reservesViewConfigId() === config.id ? 'Ausblenden' : 'Anzeigen' }}
              </button>
            </div>

            @if (reservesViewConfigId() === config.id) {
              @if (isReservesLoading()) {
                <div class="flex justify-center py-4">
                  <span class="loading loading-spinner loading-md"></span>
                </div>
              } @else if (reservesList().length === 0) {
                <div class="text-sm text-base-content/50 py-2">Keine ungenutzten Reserven vorhanden.</div>
              } @else {
                <div class="flex flex-col gap-2 border-t border-base-200 pt-2 mt-1">
                  @for (res of reservesList(); track res.technician_id + '_' + res.staging_kw) {
                    <div class="flex items-center gap-3 px-3 py-2 bg-base-200 rounded-lg">
                      <!-- KW + Farbe -->
                      <div class="shrink-0">
                        <span class="bm-badge bm-badge-neutral bm-badge-xs">KW{{ res.staging_kw }}/{{ res.staging_year }}</span>
                        @if (res.week_color) {
                          <span class="text-xs text-base-content/50 ml-1">({{ res.week_color }})</span>
                        }
                      </div>
                      <!-- Techniker -->
                      <span class="text-sm flex-1">{{ res.technician_name }}</span>
                      <!-- Zahlen -->
                      <div class="flex items-center gap-3 text-xs text-base-content/60 shrink-0">
                        <span>{{ res.shipped }} versandt</span>
                        <span>{{ res.soll }} Bedarf</span>
                      </div>
                      <!-- Überschuss -->
                      <span class="bm-badge bm-badge-warning bm-badge-xs shrink-0">+{{ res.unused }} Überschuss</span>
                      <!-- Aktion -->
                      @if (res.candidates.length > 0) {
                        <button class="bm-btn bm-btn-xs bm-btn-primary shrink-0" (click)="openRedistributeDialog(res)">
                          Umverteilen
                        </button>
                      }
                    </div>
                  }
                </div>
              }
            }
          </div>
        </div>
      }
    </div>
  </div>
}

<!-- Export-Dialog -->
@if (showExportDialog()) {
  <div class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" (click)="closeExportDialog()">
    <div class="bg-base-100 rounded-lg shadow-xl w-full max-w-md" (click)="$event.stopPropagation()">
      <div class="p-4 border-b border-base-300">
        <h3 class="text-lg font-semibold">Staging Records exportieren</h3>
      </div>

      <div class="p-4 flex flex-col gap-4">
        <!-- KW-Bereich -->
        <div>
          <label class="bm-label mb-1">Jahr</label>
          <input type="number" class="bm-input bm-input-sm w-full" [(ngModel)]="exportYear" min="2024" max="2035" />
        </div>
        <div class="flex gap-3">
          <div class="flex-1">
            <label class="bm-label mb-1">Von KW</label>
            <input type="number" class="bm-input bm-input-sm w-full" [(ngModel)]="exportKwFrom" min="1" max="53" />
          </div>
          <div class="flex-1">
            <label class="bm-label mb-1">Bis KW</label>
            <input type="number" class="bm-input bm-input-sm w-full" [(ngModel)]="exportKwTo" min="1" max="53" />
          </div>
        </div>

        <!-- Spalten-Auswahl -->
        <div>
          <label class="bm-label mb-1">Spalten</label>
          <div class="flex flex-col gap-1">
            @for (col of exportColumns; track col.key) {
              <label class="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" class="bm-checkbox bm-checkbox-sm" [(ngModel)]="col.enabled" />
                {{ col.label }}
              </label>
            }
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-4 border-t border-base-300 flex gap-2 justify-end">
        <button class="bm-btn bm-btn-ghost bm-btn-sm" (click)="closeExportDialog()">Abbrechen</button>
        <button
          class="bm-btn bm-btn-primary bm-btn-sm"
          [disabled]="exportIsLoading() || exportColumns.every(c => !c.enabled)"
          (click)="performExport()"
        >
          @if (exportIsLoading()) {
            <span class="loading loading-spinner loading-xs"></span>
          }
          <fa-icon [icon]="faDownload" class="mr-1" /> Exportieren
        </button>
      </div>
    </div>
  </div>
}

<!-- Umverteilungs-Dialog -->
@if (redistributingReserve(); as res) {
  <div class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" (click)="closeRedistributeDialog()">
    <div class="bg-base-100 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" (click)="$event.stopPropagation()">
      <div class="p-4 border-b border-base-300">
        <h3 class="text-lg font-semibold">Überschuss umverteilen</h3>
        <p class="text-sm text-base-content/60 mt-1">
          {{ res.technician_name }} — KW{{ res.staging_kw }}/{{ res.staging_year }}
          — {{ res.unused }} überschüssige Artikel
        </p>
      </div>

      <div class="p-4 flex flex-col gap-4">
        <!-- Ziel-KW -->
        <div>
          <label class="bm-label mb-1">Ziel-KW (gleicher Techniker)</label>
          <div class="flex flex-col gap-1">
            @for (c of res.candidates; track c.staging_kw) {
              <button
                class="bm-btn bm-btn-sm w-full justify-between"
                [class.bm-btn-primary]="redistributeTargetKw()?.staging_kw === c.staging_kw && redistributeTargetKw()?.year === c.year"
                [class.bm-btn-ghost]="redistributeTargetKw()?.staging_kw !== c.staging_kw || redistributeTargetKw()?.year !== c.year"
                (click)="selectRedistributeTarget(c)"
              >
                <span>
                  KW {{ c.staging_kw }}/{{ c.year }}
                  @if (c.week_color) { ({{ c.week_color }}) }
                </span>
                <span class="bm-badge bm-badge-xs bm-badge-neutral">noch {{ c.remaining }}</span>
              </button>
            }
          </div>
        </div>

        <!-- Anzahl -->
        @if (redistributeTargetKw(); as target) {
          <div>
            <label class="bm-label mb-1">Anzahl (max. {{ res.unused < target.remaining ? res.unused : target.remaining }})</label>
            <input
              type="number"
              class="bm-input bm-input-sm w-full"
              [(ngModel)]="redistributeCount"
              [min]="1"
              [max]="res.unused < target.remaining ? res.unused : target.remaining"
            />
          </div>
        }
      </div>

      <!-- Footer -->
      <div class="p-4 border-t border-base-300 flex gap-2 justify-end">
        <button class="bm-btn bm-btn-ghost bm-btn-sm" (click)="closeRedistributeDialog()">Abbrechen</button>
        <button
          class="bm-btn bm-btn-primary bm-btn-sm"
          [disabled]="!redistributeTargetKw() || isRedistributing() || redistributeCount < 1"
          (click)="performRedistribute()"
        >
          @if (isRedistributing()) {
            <span class="loading loading-spinner loading-xs"></span>
          }
          Umverteilen
        </button>
      </div>
    </div>
  </div>
}

<!-- Filial-Picker für die Ticket-Zusammenfassung -->
@if (showStorePicker()) {
  <app-store-picker
    [projectId]="projectId"
    (storeSelected)="onSummaryStoreSelected($event)"
    (closed)="showStorePicker.set(false)"
  />
}
